/* ============================================================
   A JOURNEY THROUGH LOVE — page behaviour
   Port of concept-f-gallery-wall.html's inline script. Same logic, four
   deliberate differences:

   1. Selectors are namespaced `jtl-`, and so are the toggled classes
      (jtl-in / jtl-on / jtl-away / jtl-on-dark).
   2. THE HEADER SEAM IS WIRED. measureHeader() writes the theme header's
      live bottom edge into --gh-header-h every painted frame, and its
      resting height into --gh-header-static once per layout.
      sections/header-group.json runs sticky_header_type:"on-scroll-up", so
      the header is in flow at the top, sticky when revealed and translated
      away when hidden — one static number would be wrong most of the time.
      The prototype hard-coded 0px, correct for a standalone file only.
   3. Listeners are delegated at document level and the node lists are
      re-collected on shopify:section:load, so the theme editor can add,
      move or reorder a chapter without a reload.
   4. Chapter jumps are filtered to `a[href^="#"]` INSIDE a `.jtl` subtree.
      The theme's own chrome keeps its default behaviour; the prototype had
      no other chrome to protect.

   Everything else is the prototype's: the reveal, the rail's per-row
   on-dark flip, railAway() over a full-bleed release, the running-head
   rule that empties the bar over a chapter opening, the deep-link
   orientation aid, the index sheet and the film cue.

   No libraries. Nothing external. Nothing written to any other page.
   ============================================================ */
(function () {
  'use strict';

  var root   = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var wide   = window.matchMedia('(min-width: 1200px)');
  var noanim = /noanim/.test(window.location.search);

  var chapters  = [];
  var heads     = [];
  var railRows  = [];
  var railLinks = [];
  var railFills = [];
  var barTicks  = [];
  var bleeds    = [];
  var rail, cbar, cbarNum, cbarTitle, titlePlate, interludes, toggle, sheet, headerEl;

  var barNum   = null;
  var barTitle = null;
  var deeplink = false;
  var lastHH   = null;
  var io       = null;

  // THE OPEN FILM. One film at a time is the rule, so one slot is enough —
  // and holding the open one HERE is what lets a second cue put the first
  // film's poster back. Binding the restore to `ended` alone stranded an
  // abandoned film as a frozen mid-frame with its cue gone and no way back:
  // a paused film never ends, so the listener was never unreachable, just
  // never reached. Audited and confirmed 2026-08-26.
  var openFilm = null;
  var filmSay  = null;

  function q(sel)  { return document.querySelector(sel); }
  function qa(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function docTop(el) { return el.getBoundingClientRect().top + window.pageYOffset; }

  /* ---------- 0. The header seam ---------------------------------------- */
  function measureHeaderStatic() {
    if (!headerEl) return;
    root.style.setProperty('--gh-header-static', Math.round(headerEl.offsetHeight) + 'px');
  }
  function measureHeader() {
    if (!headerEl) return;
    // the live bottom edge, clamped at 0 so a hidden header reserves nothing
    var hh = Math.max(0, Math.round(headerEl.getBoundingClientRect().bottom));
    if (hh !== lastHH) {
      lastHH = hh;
      root.style.setProperty('--gh-header-h', hh + 'px');
    }
  }

  /* ---------- 1. Reveals — IO only, brief timing ------------------------ */
  function revealAll() {
    qa('.jtl-rv').forEach(function (el) { el.classList.add('jtl-in'); });
  }
  function observeReveals() {
    var els = qa('.jtl-rv').filter(function (el) { return !el.hasAttribute('data-jtl-seen'); });
    if (!els.length) return;
    if (reduce.matches || noanim || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.setAttribute('data-jtl-seen', ''); el.classList.add('jtl-in'); });
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('jtl-in');
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -50px 0px', threshold: 0.01 });
    }
    els.forEach(function (el) { el.setAttribute('data-jtl-seen', ''); io.observe(el); });
  }

  /* ---------- 2. Chapter tracking: rail + bar --------------------------- */
  function collect() {
    chapters   = qa('[data-jtl-chapter]');
    heads      = chapters.map(function (c) { return c.querySelector('.jtl-ch-head'); });
    railRows   = qa('.jtl-rail li');
    railLinks  = qa('.jtl-rail a');
    railFills  = railLinks.map(function (a) { return a.querySelector('.jtl-rail__bar i'); });
    barTicks   = qa('#jtl-cbar-ticks i');
    bleeds     = qa('.jtl-release__frame');
    rail       = document.getElementById('jtl-rail');
    cbar       = document.getElementById('jtl-cbar');
    cbarNum    = document.getElementById('jtl-cbar-num');
    cbarTitle  = document.getElementById('jtl-cbar-title');
    titlePlate = document.getElementById('jtl-title-plate');
    interludes = qa('.jtl-interlude');
    toggle     = document.getElementById('jtl-cbar-toggle');
    sheet      = document.getElementById('jtl-sheet');
    headerEl   = q('.section-header') || q('#shopify-section-header');
    barNum     = null;
    barTitle   = null;
    measureHeaderStatic();
  }

  /* The rail stands aside for a full-bleed release.
     Over the black interlude the rail flips its own colours row by row,
     which works because that ground is one known value. A photograph is
     not: no fixed pair of colours can be trusted across it, and a
     paper-white tab behind the rail is the fault the audit removed. So
     while a release plate is behind the rail, the rail leaves — and the
     release is uninterrupted. It never leaves while one of its own links
     holds focus, so the keyboard can never lose its place. */
  function railAway() {
    if (!rail || !wide.matches) return;
    var rr = rail.getBoundingClientRect();
    var over = false;
    for (var i = 0; i < bleeds.length; i++) {
      var br = bleeds[i].getBoundingClientRect();
      if (br.top < rr.bottom && br.bottom > rr.top) { over = true; break; }
    }
    var held = rail.contains(document.activeElement);
    var off  = over && !held;
    rail.classList.toggle('jtl-away', off);
    rail.setAttribute('aria-hidden', off ? 'true' : 'false');
  }

  function paint() {
    measureHeader();
    if (!chapters.length) return;

    var mid = window.pageYOffset + window.innerHeight * 0.38;
    // -1 until the first chapter's top is reached: on the title plate and
    // the foreword no chapter is current, and nothing claims aria-current
    var idx = -1;
    for (var i = 0; i < chapters.length; i++) if (docTop(chapters[i]) <= mid) idx = i;

    var p = 0;
    if (idx >= 0) {
      var top = docTop(chapters[idx]);
      var h   = chapters[idx].offsetHeight || 1;
      p = Math.max(0, Math.min(1, (mid - top) / h));
    }
    // reduced motion: the progress fill steps between states instead of
    // growing continuously with scroll position
    var still = reduce.matches;

    for (var j = 0; j < chapters.length; j++) {
      var fill = 0;
      if (idx >= 0) fill = j < idx ? 100 : (j === idx ? (still ? 100 : p * 100) : 0);
      if (railFills[j]) railFills[j].style.width = fill + '%';
      if (barTicks[j])  barTicks[j].style.width  = fill + '%';
      if (railLinks[j]) railLinks[j].setAttribute('aria-current', j === idx ? 'true' : 'false');
    }

    // THE RUNNING HEAD. While a chapter's own opening — its label and its
    // large head — is on screen, the bar withdraws its number and title and
    // keeps only the index control, the way a book suppresses the running
    // head on a chapter's opening page: the same words at two sizes in one
    // screen was the fault this guards against. Before chapter one the bar
    // is likewise blank; nothing is claimed on the foreword.
    var neutral = idx < 0;
    if (!neutral) {
      for (var m = 0; m < heads.length; m++) {
        if (!heads[m]) continue;
        var hr = heads[m].getBoundingClientRect();
        if (hr.bottom > 0 && hr.top < window.innerHeight) { neutral = true; break; }
      }
    }
    var bNum   = neutral ? '' : (chapters[idx].getAttribute('data-num') || '');
    var bTitle = neutral ? '' : (chapters[idx].getAttribute('data-title') || '');
    if (cbarNum   && bNum   !== barNum)   { barNum   = bNum;   cbarNum.textContent   = bNum; }
    if (cbarTitle && bTitle !== barTitle) { barTitle = bTitle; cbarTitle.textContent = bTitle; }

    // the rail carries no ground of its own; each row takes the colours of
    // the ground actually behind it, so it never cuts a hole in the black
    // interlude
    if (interludes.length && railRows.length) {
      for (var k = 0; k < railRows.length; k++) {
        var rr = railRows[k].getBoundingClientRect();
        var onDark = false;
        for (var m = 0; m < interludes.length; m++) {
          var ir = interludes[m].getBoundingClientRect();
          if (ir.top < rr.bottom && ir.bottom > rr.top) { onDark = true; break; }
        }
        railRows[k].classList.toggle('jtl-on-dark', onDark);
      }
    }

    railAway();

    // the bar exists only once the title plate has been left behind — and it
    // is held on for as long as its own index sheet is open
    if (!cbar) return;
    var past = titlePlate
      ? window.pageYOffset > (docTop(titlePlate) + titlePlate.offsetHeight - 90)
      : window.pageYOffset > 0;
    var held = toggle && (toggle.getAttribute('aria-expanded') === 'true' ||
                          document.activeElement === toggle);
    cbar.classList.toggle('jtl-on', past || deeplink || held);
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () { paint(); ticking = false; });
  }
  function onResize() { measureHeaderStatic(); onScroll(); }

  /* ---------- 3. Index sheet (small screens) ----------------------------- */
  function setSheet(on) {
    if (!sheet || !toggle) return;
    sheet.classList.toggle('jtl-on', on);
    sheet.setAttribute('aria-hidden', on ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
    // the sheet can never hang over the hero with its own bar off-screen
    if (on) { if (cbar) cbar.classList.add('jtl-on'); }
    else paint();
  }

  /* ---------- 4. Arriving mid-page from a shared link -------------------- */
  function deepLink() {
    var h = window.location.hash;
    if (!h || h === '#top') return;
    var t = null;
    try { t = document.querySelector(h); } catch (err) { return; }
    if (!t || !t.hasAttribute('data-jtl-chapter')) return;
    var back = document.getElementById('jtl-cbar-back');
    if (!back) return;
    deeplink = true;
    back.hidden = false;
    var timer;
    var retire = function () {
      back.hidden = true; deeplink = false;
      window.clearTimeout(timer);
    };
    timer = window.setTimeout(retire, 14000);
    // the orientation aid retires as soon as the reader takes over
    window.setTimeout(function () {
      window.addEventListener('scroll', retire, { passive: true, once: true });
    }, 1200);
    back.addEventListener('click', function () { back.hidden = true; });
  }

  /* ---------- 5. Delegated listeners, bound exactly once ----------------- */
  // A screen reader was told nothing at all when a film opened, and the
  // cue that carried aria-expanded is display:none while it is playing, so
  // it is out of the accessibility tree exactly when it has something to
  // say. One polite region on the .jtl root covers all three chapters.
  function say(msg) {
    if (!filmSay) {
      var host = q('.jtl') || document.body;
      filmSay = document.createElement('p');
      filmSay.className = 'jtl-vh';
      filmSay.setAttribute('aria-live', 'polite');
      host.appendChild(filmSay);
    }
    filmSay.textContent = msg;
  }

  // preventScroll on EVERY focus() here. focus() drags the viewport, because
  // scroll-padding-top is 170px for the sticky chapter bar and the top of a
  // 9:16 figure usually sits inside that zone — measured 120-296px of jump
  // when a film opened, at every width from 360 to 1600. The wall itself
  // never reflowed; the page moved under the reader instead.
  function focusQuietly(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (err) { try { el.focus(); } catch (err2) {} }
  }

  function closeFilm(refocus) {
    if (!openFilm) return;
    var f = openFilm;
    openFilm = null;
    try { f.vid.pause(); } catch (err) {}
    f.vid.removeEventListener('ended', f.onEnded);
    // back to the first frame: the cue is a PLAY mark, and pressing play on a
    // composed still and getting the middle of a film reads as a fault.
    try { f.vid.currentTime = 0; } catch (err) {}
    f.host.hidden = true;
    f.cue.hidden = false;
    f.cue.setAttribute('aria-expanded', 'false');
    say('Film closed.');
    if (refocus) focusQuietly(f.cue);
  }

  function bind() {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // a shared link lands the browser mid-page before layout settles:
    // repaint the rail and the bar so the reader is oriented on the first
    // frame they see
    window.addEventListener('load', function () { measureHeaderStatic(); paint(); });
    window.addEventListener('hashchange', paint);
    // focus moving into or out of the rail changes whether it may stand aside
    document.addEventListener('focusin', railAway);
    window.setTimeout(paint, 80);
    window.setTimeout(paint, 400);

    // the OS setting can be flipped mid-session
    function onReduceChange() { if (reduce.matches) revealAll(); paint(); }
    if (reduce.addEventListener) reduce.addEventListener('change', onReduceChange);
    else if (reduce.addListener) reduce.addListener(onReduceChange);

    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;

      // the chapter bar's own toggle
      if (e.target.closest('#jtl-cbar-toggle')) {
        setSheet(toggle && toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      // the film's close mark. Ahead of the cue: a press here is a way OUT,
      // and until it existed the only exit from an open film was watching it
      // through to the end.
      if (e.target.closest('.jtl-film-close')) { closeFilm(true); return; }

      // the film cue — the film plays in place, over its own poster frame.
      // The cue is only rendered when a film is attached (journey-film.liquid),
      // so a press here always has something to play.
      var play = e.target.closest('.jtl-play');
      if (play) {
        var pid  = play.getAttribute('aria-controls');
        var host = pid ? document.getElementById(pid) : null;
        if (!host) return;
        var vid = host.querySelector('video');
        if (!vid) return;

        // one film at a time — and the one already open is put back to its
        // poster, not merely silenced. Pausing it and walking away was the
        // whole of the stranded-frame fault.
        closeFilm(false);

        // THE POSTER, FROM THE WALL'S OWN STILL. A <video controls> with no
        // loaded frame paints the UA's opaque grey box and a spinner over the
        // still beneath it — 265ms unthrottled, 8.8s on Slow 3G (measured
        // 2026-08-26); a transparent ground reveals nothing. The markup carries
        // no poster= because that fetched a second 900w copy for every film on
        // load; currentSrc is the copy the page already decoded, so this costs
        // no bytes until a cue is pressed.
        //
        // AND IT WAITS FOR THE STILL. currentSrc is empty until the lazy still
        // has begun loading, and a poster assigned before the image has decoded
        // paints nothing, so `if (still.currentSrc)` alone left the grey box in
        // place on exactly the path that needed it: the chapter rail deep-links
        // straight to a chapter, and a cue pressed within a second of that jump
        // finds its own still still in flight. Measured 2026-09-03 at 390 on a
        // cold cache — Slow 3G, rail jump to chapter three, press at 0.2s and
        // 0.8s: poster unset, readyState 0, i.e. the UA's grey box for the whole
        // buffering window. `complete` rather than currentSrc for the same
        // reason, and a one-shot load listener closes the gap the moment the
        // still lands — the still is ~25KB against a 7.2Mbps film, so it always
        // arrives first. Still zero bytes of its own: this is the copy the wall
        // was fetching anyway.
        var fig   = host.closest('.jtl-film-fig') || host.parentElement;
        var still = fig ? fig.querySelector('img') : null;
        if (still) {
          if (still.complete && still.currentSrc) {
            vid.poster = still.currentSrc;
          } else {
            still.addEventListener('load', function () {
              if (still.currentSrc) vid.poster = still.currentSrc;
            }, { once: true });
          }
        }

        host.hidden = false;
        play.hidden = true;
        play.setAttribute('aria-expanded', 'true');

        var onEnded = function () { closeFilm(true); };
        openFilm = { host: host, cue: play, vid: vid, onEnded: onEnded };
        vid.addEventListener('ended', onEnded, { once: true });
        say('Film playing.');

        focusQuietly(vid);
        var pr = vid.play();
        // Autoplay policy can still refuse; the press was the gesture, so this
        // is belt and braces. If it refuses, leave the player open with its
        // own controls rather than snapping the poster back under the visitor.
        if (pr && pr.catch) { pr.catch(function () {}); }
        return;
      }

      // chapter jumps
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      // only ours: the theme's own chrome must keep its default behaviour
      if (!a.closest('.jtl')) return;
      var id = a.getAttribute('href');
      if (id === '#') return;
      var t = id === '#top' ? document.body : document.querySelector(id);
      if (!t) return;
      e.preventDefault();
      setSheet(false);
      if (id === '#top') {
        window.scrollTo({ top: 0, behavior: reduce.matches ? 'auto' : 'smooth' });
      } else {
        t.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' });
      }
      if (history.replaceState) history.replaceState(null, '', id === '#top' ? location.pathname : id);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      // a film first: it is the thing laid over the page, so it is the thing
      // Escape should take back
      if (openFilm) { closeFilm(true); return; }
      if (!toggle || toggle.getAttribute('aria-expanded') !== 'true') return;
      // focus first: the bar is held on while its own control has focus, so
      // closing can never pull the toggle out from under the keyboard
      toggle.focus();
      setSheet(false);
    });

    document.addEventListener('focusout', function (e) {
      // once focus leaves the toggle the bar goes back to following the
      // scroll; and once it leaves the rail the rail may stand aside again
      if (toggle && e.target === toggle) window.setTimeout(paint, 0);
      window.setTimeout(railAway, 0);
    });

    // the sheet closes behind any link taken from it
    document.addEventListener('click', function (e) {
      if (sheet && e.target.closest && e.target.closest('#jtl-sheet a')) setSheet(false);
    });

    // the theme editor swaps a section in without a reload
    document.addEventListener('shopify:section:load', function () {
      collect();
      observeReveals();
      paint();
    });
    document.addEventListener('shopify:section:unload', function () {
      // the customiser can take the section holding the open film with it
      if (openFilm && !document.contains(openFilm.host)) openFilm = null;
      collect();
      paint();
    });
  }

  function boot() {
    collect();
    observeReveals();
    if (reduce.matches || noanim || !('IntersectionObserver' in window)) revealAll();
    deepLink();
    bind();
    paint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
